import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { INDUSTRIES } from "@/data/site";
import { EASE, Reveal, SectionHead } from "@/components/motion/Reveal";
import { cn } from "@/lib/utils";

const Frame = ({ item }) => (
  <div className="relative h-[380px] overflow-hidden rounded-[28px] border border-white/5 bg-ink-800 sm:h-[520px]" data-testid="industry-preview">
    <AnimatePresence mode="popLayout">
      <motion.img
        key={item.slug}
        src={item.img}
        alt={item.title}
        initial={{ opacity: 0, scale: 1.12, clipPath: "inset(0 0 0 100%)" }}
        animate={{ opacity: 1, scale: 1, clipPath: "inset(0 0 0 0%)" }}
        exit={{ opacity: 0 }}
        transition={{ duration: 1, ease: EASE }}
        className="absolute inset-0 h-full w-full object-cover"
      />
    </AnimatePresence>
    <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/30 to-transparent" />
    <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
      <p className="text-base leading-relaxed text-slate-200">{item.blurb}</p>
      <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Recommended</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {item.picks.map((p) => (
          <span key={p} className="rounded-full border border-volt/30 bg-volt/5 px-3 py-1 text-xs text-volt">{p}</span>
        ))}
      </div>
    </div>
  </div>
);

export const Industries = ({ index = "02" }) => {
  const [active, setActive] = useState(0);
  return (
    <section className="relative border-t border-white/5 bg-ink-900 py-28 lg:py-40" data-testid="industries-section">
      <div className="container-x">
        <SectionHead index={index} eyebrow="Industries" title="Built for Your Business">
          From a single claw machine at the register to a full entertainment floor — equipment matched to how your customers spend their time.
        </SectionHead>
        <div className="mt-16 grid gap-10 lg:grid-cols-12">
          <ul className="lg:col-span-6">
            {INDUSTRIES.map((it, i) => (
              <Reveal key={it.slug} delay={i * 0.05}>
                <button
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  onClick={() => setActive(i)}
                  className="group flex w-full items-center gap-6 border-b border-white/10 py-6 text-left"
                  data-testid={`industry-item-${it.slug}`}
                >
                  <span className={cn("font-mono text-xs transition-colors", active === i ? "text-volt" : "text-slate-600")}>0{i + 1}</span>
                  <span className={cn("flex-1 font-display text-3xl font-bold tracking-tight transition-[color,transform] duration-500 sm:text-5xl", active === i ? "translate-x-2 text-white" : "text-slate-600 group-hover:text-slate-300")}>
                    {it.title}
                  </span>
                  <ArrowUpRight className={cn("h-6 w-6 transition-[opacity,transform] duration-500", active === i ? "rotate-45 text-volt opacity-100" : "opacity-0")} />
                </button>
              </Reveal>
            ))}
          </ul>
          <div className="lg:col-span-6">
            <div className="lg:sticky lg:top-28">
              <Frame item={INDUSTRIES[active]} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
